import React, { memo, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";
import { Box, Button, Dialog, Divider, Typography } from "@mui/material";
import { AppConstant, KeyConstant, LangConstant, SystemConstant } from "const";
import { useDispatch, useSelector } from "react-redux";
import UploadDocument from "./UploadDocument";
import { saveMessageInQueue, useConversationContext } from "../ConversationContext";
import UploadMedia from "./UploadMedia";
import { ConversationActions } from "redux-store";
import { ConversationSelectors, DEFAULT_ATTACHMENT } from "redux-store/conversation.redux";
import { AttachmentUtil, FileUtil, MultipleFileUtil, StorageUtil, convertHex2rgba, isImage, isVideo } from "utils";
import { getCommonLang, getNSLang } from "utils/lang.utils";
import { getInteractor } from "services/local.service";
import MessengerChatInput from "../MessengerChatInput";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { useAlertContext } from "components/context/AlertContext";
import { Processing } from "components";
import { checkEncryptWithIvs } from "utils/message.utils";

const getProtocol = window.bglobalSignal.libs.util.getProtocol;
const getProtocolGroup = window.bglobalSignal.libs.util.getProtocolGroup;
const findEncryptTypeProtocol = window.bglobalSignal.libs.EncryptTypeProtocol.find;
const getFileProtocol = window.bglobalSignal.libs.util.getFileProtocol;

const MAX_UPLOADING = 9;
const UploadFileDialog = ({ isMediaType, multipleFile, onClose }) => {
  const hasMultipleFile = multipleFile.length > 0;
  const isUploadMedia = Boolean(hasMultipleFile && isMediaType);
  const isUploadFile = Boolean(hasMultipleFile && !isMediaType);
  const classes = useStyles();
  const dispatch = useDispatch();
  const { groupDetail } = useConversationContext();
  const prefixKey = StorageUtil.getCurrentPrefixKey();
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID);
  const { showAlert } = useAlertContext();
  const threadingId = useSelector(ConversationSelectors.getThreadingId);
  const sendOpt = useSelector(ConversationSelectors.getSendOpt);

  const [uploadFiles, setUploadFiles] = useState(multipleFile);
  const [isLoading, setIsLoading] = useState(false);

  const handleCloseDialog = () => {
    dispatch(
      ConversationActions.conversationSet({
        uploadingAttachment: DEFAULT_ATTACHMENT,
      }),
    );

    onClose();
  };

  const handleConfirm = async (caption = null, mentionIds, options) => {
    setIsLoading(true);
    try {
      const savingFiles = [];
      for (let index = 0; index < uploadFiles.length; index++) {
        const fileItem = uploadFiles[index];
        const fileType = fileItem.file.type || AppConstant.DEFAULT_MIME_TYPE;
        // Compress image file before saving
        const compressedFile = isImage(fileType) ? await FileUtil.compressImageFile(fileItem.file) : fileItem.file;
        const savingPath = await AttachmentUtil.saveFile2Local(fileItem.attachmentId, compressedFile, groupDetail.id, {
          name: fileItem.file.name,
        });

        if (!savingPath) throw new Error("NOT_SAVING_FILE");

        savingFiles.push({
          ...fileItem,
          file: {
            ...compressedFile,
            name: fileItem.file.name,
            type: fileType,
            path: savingPath,
            size: compressedFile.size,
          },
        });
      }
      const memberIdArray = groupDetail.groupMembers.map(member => member.id) || [];
      const deviceList = await getInteractor(prefixKey).LocalDeviceService.find({
        account_id: memberIdArray,
        state: SystemConstant.STATE.active,
      });
      const deviceProtocols = new Set();
      await Promise.all(
        deviceList.map(async device => {
          const encryptVersion = await getProtocol(prefixKey, device);
          const encryptTypeProtocol = findEncryptTypeProtocol(encryptVersion);
          deviceProtocols.add(encryptTypeProtocol);
        }),
      );
      const encryptTypeProtocol = getProtocolGroup(prefixKey, Array.from(deviceProtocols));

      // Encryption message content and save file info in file.table
      const fileEncryptionParams = getFileProtocol(deviceList, encryptTypeProtocol);

      let isEncryptWithIvs = true;
      const deviceOldVersion = deviceList.find(device => false === Boolean(checkEncryptWithIvs(device)));
      if (deviceOldVersion) isEncryptWithIvs = false;

      const encryptionResults = await MultipleFileUtil.encryptMultipleFile(
        isEncryptWithIvs,
        savingFiles,
        fileEncryptionParams,
      );

      if (encryptionResults) {
        await getInteractor().LocalFileService.saveMultipleFile(
          encryptionResults,
          SystemConstant.UPLOAD_TYPE.group,
          accountId,
          groupDetail.id,
        );

        // Send request to handle message in queue
        const messageContent = {
          attachment_id: "",
          meta_data: "",
          aes_key_info: "",
          multiple_file: encryptionResults,
        };
        if (caption && typeof caption === "string") {
          messageContent.content_message = caption.trim();
        }
        await handleSendMessage(messageContent, mentionIds, options);
      } else {
        throw new Error("ENCRYPTION_ERROR");
      }
    } catch (error) {
      console.error(error);
      showAlert({ content: getCommonLang("TXT_SERVER_ERROR"), alertProps: { severity: "error" } });
    }

    setIsLoading(false);
  };

  const handleSendMessage = async (content, mentionIds, options = {}) => {
    const sendType = isMediaType ? SystemConstant.SEND_TYPE.image : SystemConstant.SEND_TYPE.file;

    options.send_as_direct_message = sendOpt;

    await saveMessageInQueue({
      prefixKey,
      groupId: groupDetail.id,
      threadId: threadingId,
      sendType: sendType,
      content: JSON.stringify(content),
      mentionIds: mentionIds,
      options,
    });

    dispatch(
      ConversationActions.conversationSet({
        sendAsDirectMsgOpt: SystemConstant.THREAD_MSG_SEND_OPTION.notSendAsDirect,
      }),
    );

    handleCloseDialog();
  };

  const handleChangeFile = async removedFile => {
    const newUploadList = uploadFiles.filter(item => item.attachmentId !== removedFile.attachmentId);
    if (newUploadList.length === 0) handleCloseDialog();
    setUploadFiles(newUploadList);
  };

  const isSingleMediaFile =
    isUploadMedia &&
    multipleFile.length === 1 &&
    (isImage(multipleFile[0].file.type) || isVideo(multipleFile[0].file.type));
  const isDisabledButton = uploadFiles.length > MAX_UPLOADING || isLoading;

  if (isLoading) {
    return <Processing isShow={true} />;
  }

  return (
    <Dialog
      open={hasMultipleFile}
      classes={{
        paper: classes.dialogPaper,
      }}
    >
      <Box>
        {isUploadFile && (
          <UploadDocument multipleFile={multipleFile} onChangeFile={handleChangeFile} onClose={handleCloseDialog} />
        )}

        {isUploadMedia && (
          <UploadMedia multipleFile={multipleFile} onChangeFile={handleChangeFile} onClose={handleCloseDialog} />
        )}
      </Box>

      {isSingleMediaFile ? (
        <Box p={2}>
          <MessengerChatInput
            placeholder={getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_INPUT_CAPTION")}
            isAllowAttach={false}
            onSendMessage={handleConfirm}
            isMediaUpload={true}
          />
        </Box>
      ) : (
        <>
          <Divider />
          <Box className={classes.actionContainer} px={2}>
            <TotalUploadingFiles count={uploadFiles.length} />
            <Button
              className={classes.actionBtn}
              variant="contained"
              disabled={isDisabledButton}
              onClick={handleConfirm}
            >
              {getCommonLang(LangConstant.TXT_CONFIRM)}
            </Button>
          </Box>
        </>
      )}
    </Dialog>
  );
};

UploadFileDialog.propTypes = {
  isMediaType: PropTypes.bool,
  multipleFile: PropTypes.any.isRequired,

  onClose: PropTypes.func.isRequired,
};

export default memo(UploadFileDialog);

const TotalUploadingFiles = ({ count }) => {
  const { t: getLabel } = useTranslation();
  const classes = useStyles();

  return (
    <Typography
      className={clsx(classes.totalText, count > MAX_UPLOADING && "warning")}
      dangerouslySetInnerHTML={{ __html: getLabel("FM_TOTAL", { count, limit: MAX_UPLOADING }) }}
    />
  );
};

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    width: "100%",
  },

  loadingBox: {
    width: "100%",
    height: "100%",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    background: convertHex2rgba("#808080", 0.9),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: theme.zIndex.tooltip + 1,
  },

  actionContainer: {
    height: 64,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  actionBtn: {
    // width: 120,
    padding: "9px 16px",
    borderRadius: 10,
  },

  totalText: {
    "&.warning": {
      "& span": {
        color: "#FF0000",
        fontSize: "1rem",
      },
    },
  },
}));
